import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCloudUploadAlt, FaPlus, FaSpinner, FaTimes } from 'react-icons/fa';
import { authService, categoryService, productService } from '../../../../services/apiService';
import { toast } from 'react-toastify';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const commaIndex = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const MAX_PRODUCT_IMAGES = 5;

const EMPTY_FORM = {
  name: '',
  partNumber: '',
  rootCategoryId: '',
  brand: '',
  specs: {}
};

/** @param {Array} flat - from /categories/flat?forVendor=true */
export function buildVehicleCompatibilityForCatalogModel(makerName, mod) {
  const y = mod?.generationYear != null && mod.generationYear !== '' ? String(mod.generationYear) : '';
  return [
    {
      make: String(makerName || '').trim(),
      model: String(mod?.name || '').trim(),
      yearFrom: y,
      yearTo: y,
      variant: String(mod?.variant || '').trim() || 'All',
      fuelType: String(mod?.fuelType || '').trim() || 'All'
    }
  ];
}

function normalizeFlatList(result) {
  let list = [];
  if (result?.categories && Array.isArray(result.categories)) list = result.categories;
  else if (Array.isArray(result)) list = result;
  else if (result?.data?.categories && Array.isArray(result.data.categories)) list = result.data.categories;
  else if (result?.data && Array.isArray(result.data)) list = result.data;
  return list.map((c) => ({
    ...c,
    id: String(c.id || c._id || '')
  }));
}

/**
 * Superadmin marketplace product form (POST /admin/products).
 * @param {{ vehicleCompatibility: object[], compact?: boolean, onSuccess?: () => void, disableSubmit?: boolean, catalogModelId?: string }} props
 */
const MarketplaceProductForm = ({
  vehicleCompatibility,
  compact = false,
  onSuccess,
  disableSubmit = false,
  catalogModelId = ''
}) => {
  const [flatCategories, setFlatCategories] = useState([]);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [specFields, setSpecFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const categoryCacheRef = useRef(new Map());

  useEffect(() => {
    if (!catalogModelId || String(catalogModelId).trim() === '') {
      setFlatCategories([]);
      setForm((f) => ({ ...f, rootCategoryId: '', specs: {} }));
      setLoadingCategories(false);
      return;
    }
    const modelId = String(catalogModelId).trim();
    const cached = categoryCacheRef.current.get(modelId);
    if (cached) {
      setFlatCategories(cached);
      setForm((f) => ({ ...f, rootCategoryId: '', specs: {} }));
      setLoadingCategories(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingCategories(true);
        const res = await categoryService.getAllActiveCategoriesFlat({
          forVendor: true,
          catalogModelId: modelId
        });
        if (!cancelled) {
          const normalized = normalizeFlatList(res);
          categoryCacheRef.current.set(modelId, normalized);
          setFlatCategories(normalized);
          setForm((f) => ({ ...f, rootCategoryId: '', specs: {} }));
        }
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Could not load categories');
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogModelId]);

  const rootCategories = useMemo(
    () => flatCategories.filter((c) => !c.parentCategory).sort((a, b) => a.name.localeCompare(b.name)),
    [flatCategories]
  );

  const selectedRoot = useMemo(() => {
    if (!form.rootCategoryId) return null;
    return rootCategories.find((c) => String(c.id || c._id) === String(form.rootCategoryId)) || null;
  }, [form.rootCategoryId, rootCategories]);

  const rootCategoryName = selectedRoot?.name || '';

  useEffect(() => {
    if (!rootCategoryName) {
      setSpecFields([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await productService.getProductSpecTemplate({
          category: rootCategoryName,
          subCategory: ''
        });
        if (!cancelled) setSpecFields(data?.fields || []);
      } catch {
        if (!cancelled) setSpecFields([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootCategoryName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disableSubmit) {
      toast.error('Select car maker, model, and generation first.');
      return;
    }
    const name = (form.name || '').trim();
    const partNumber = (form.partNumber || '').trim();
    const brand = (form.brand || '').trim();
    if (!rootCategoryName) {
      toast.error('Select a category');
      return;
    }
    if (!name || !partNumber || !brand) {
      toast.error('Fill part name, part number, and brand.');
      return;
    }

    try {
      setSaving(true);
      await productService.createAdminProduct({
        name,
        partNumber,
        category: rootCategoryName,
        brand,
        // Hidden defaults to keep payload valid while simplifying this modal UX.
        price: 1,
        stock: 1,
        description: `${name} (${partNumber})`,
        class: 'Vehicle-specific',
        catalogManaged: true,
        partType: 'AUTO',
        vehicleCompatibility: Array.isArray(vehicleCompatibility) ? vehicleCompatibility : [],
        specs: form.specs || {},
        images: imageUrls.filter(Boolean)
      });
      toast.success('Product added (marketplace listing).');
      setForm({ ...EMPTY_FORM });
      setImageUrls([]);
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Could not create product');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = compact
    ? 'block text-[10px] font-semibold text-gray-500 uppercase mb-0.5'
    : 'block text-xs font-semibold text-gray-600 mb-1';
  const inputCls = compact
    ? 'w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

  const onPickProductImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MAX_PRODUCT_IMAGES - imageUrls.length;
    if (remaining <= 0) {
      toast.warning(`Maximum ${MAX_PRODUCT_IMAGES} images per product.`);
      return;
    }
    const slice = files.slice(0, remaining);
    if (files.length > slice.length) {
      toast.warning(`Only ${slice.length} image(s) added (max ${MAX_PRODUCT_IMAGES} total).`);
    }
    try {
      setUploadingImages(true);
      const uploaded = [];
      for (const file of slice) {
        const base64 = await readFileAsBase64(file);
        const url = await authService.uploadFile(
          base64,
          file.name,
          file.type || 'image/jpeg'
        );
        uploaded.push(String(url || '').trim());
      }
      const merged = [...imageUrls, ...uploaded.filter(Boolean)];
      setImageUrls(merged);
      if (uploaded.length) toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded.`);
    } catch (err) {
      toast.error(err.message || 'Could not upload image(s).');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImageAt = (index) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <form onSubmit={onSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      <div>
        <label className={labelCls}>Product images (optional)</label>
        <div
          className={
            compact
              ? 'border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50/80'
              : 'border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50/80'
          }
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-violet-700 shrink-0">
              <FaCloudUploadAlt className={compact ? 'text-xl' : 'text-2xl'} />
              <label className="cursor-pointer text-xs font-semibold hover:text-violet-900">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploadingImages || saving}
                  onChange={onPickProductImages}
                />
                {uploadingImages ? 'Uploading…' : 'Upload images'}
              </label>
            </div>
            <p className="text-[10px] text-gray-500 sm:flex-1">
              PNG or JPG. Up to {MAX_PRODUCT_IMAGES} images; shown on the marketplace listing.
            </p>
          </div>
          {imageUrls.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
              {imageUrls.map((url, index) => (
                <div key={`${url}-${index}`} className="relative group">
                  <img
                    loading="lazy"
                    src={url}
                    alt=""
                    className="w-full h-16 sm:h-20 object-cover rounded-lg border border-gray-200 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => removeImageAt(index)}
                    className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-red-500 text-white opacity-90 hover:opacity-100 shadow-sm"
                    aria-label="Remove image"
                  >
                    <FaTimes className="text-[10px]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2' : 'grid grid-cols-1 md:grid-cols-2 gap-3'}>
        <div className={compact ? 'sm:col-span-2' : 'md:col-span-2'}>
          <label className={labelCls}>
            Part name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className={labelCls}>
            Part number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.partNumber}
            onChange={(e) => setForm((f) => ({ ...f, partNumber: e.target.value }))}
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className={labelCls}>
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={form.rootCategoryId}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                rootCategoryId: e.target.value,
                specs: {}
              }))
            }
            className={inputCls}
            required
            disabled={!catalogModelId || loadingCategories}
          >
            <option value="">
              {!catalogModelId
                ? '— Select maker & model above first —'
                : loadingCategories
                  ? '— Loading categories… —'
                  : rootCategories.length === 0
                    ? '— No categories for this model —'
                    : '— Select root category —'}
            </option>
            {rootCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {catalogModelId
              ? 'Only categories assigned to this model in Vehicle catalogue (model → Product categories).'
              : 'Pick maker and model first.'}
          </p>
        </div>
        <div>
          <label className={labelCls}>
            Brand <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.brand}
            onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
            className={inputCls}
            required
          />
        </div>
      </div>

      {specFields.length > 0 && (
        <div
          className={
            compact
              ? 'border border-violet-100 rounded-lg p-2 bg-white/80'
              : 'border border-gray-200 rounded-xl p-4 bg-gray-50/80'
          }
        >
          <p className={compact ? 'text-[10px] font-semibold text-gray-600 uppercase mb-2' : 'text-xs font-bold text-gray-700 mb-3'}>
            Category specifications
          </p>
          <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'grid grid-cols-1 md:grid-cols-2 gap-3'}>
            {specFields.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>
                  {f.label} <span className="text-red-500">*</span>
                </label>
                {f.type === 'select' && f.options ? (
                  <select
                    value={form.specs[f.key] || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        specs: { ...prev.specs, [f.key]: e.target.value }
                      }))
                    }
                    className={inputCls}
                    required
                  >
                    <option value="">Select…</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.specs[f.key] || ''}
                    placeholder={f.placeholder || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        specs: { ...prev.specs, [f.key]: e.target.value }
                      }))
                    }
                    className={inputCls}
                    required
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || disableSubmit}
          className={
            compact
              ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-700 text-white text-xs font-semibold hover:bg-violet-800 disabled:opacity-50'
              : 'inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-700 text-white text-sm font-semibold hover:bg-violet-800 disabled:opacity-50'
          }
        >
          {saving ? <FaSpinner className="animate-spin" /> : <FaPlus className="text-xs" />}
          Add product
        </button>
      </div>
    </form>
  );
};

export default MarketplaceProductForm;
